
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2 } from "lucide-react";
import { Attachment } from "./useContractFormState";

interface ContractAttachmentsProps {
  existingAttachments: Attachment[];
  setExistingAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
}

export function ContractAttachments({
  existingAttachments,
  setExistingAttachments,
  attachments,
  setAttachments
}: ContractAttachmentsProps) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setAttachments([...attachments, ...newFiles]);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (id: string) => {
    setExistingAttachments(existingAttachments.filter((a) => a.id !== id));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Attachments</h3>
        <p className="text-sm text-muted-foreground">
          Upload documents related to this contract
        </p>
      </div>

      <div className="border-2 border-dashed rounded-md p-6 text-center">
        <Input
          type="file"
          id="attachments"
          className="hidden"
          onChange={handleFileChange}
          multiple
        />
        <label
          htmlFor="attachments"
          className="cursor-pointer flex flex-col items-center"
        >
          <Upload className="h-8 w-8 text-muted-foreground mb-2" />
          <span className="font-medium">Click to upload files</span>
          <span className="text-xs text-muted-foreground mt-1">
            PDF, DOCX, XLSX, PPTX, JPG, PNG
          </span>
        </label>
      </div>

      {existingAttachments.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Existing Attachments</h4>
          {existingAttachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center justify-between bg-muted rounded-md p-2"
            >
              <div className="flex items-center space-x-2">
                <span className="font-medium text-sm">
                  {attachment.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(attachment.size / 1024)} KB
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(attachment.url, "_blank")}
                >
                  View
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => handleRemoveExistingAttachment(attachment.id || '')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Files to Upload</h4>
          {attachments.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-muted rounded-md p-2"
            >
              <div className="flex items-center space-x-2">
                <span className="font-medium text-sm">{file.name}</span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(file.size / 1024)} KB
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => handleRemoveFile(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
